import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { createPortal } from "react-dom";

import { InventoryBoxUnitKindSelect } from "@/components/inventory/InventoryBoxUnitKindSelect";
import { Button } from "@/components/ui/Button";
import { InboundZplPrinterHeaderButton } from "@/pages/shipping/InboundZplPrinterHeaderButton";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { hubNestedModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label } from "@/components/ui/Input";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useFloorPageScanCapture } from "@/lib/floorPageScanCapture";
import {
  combineInventoryBox,
  fetchInventoryBoxEvents,
  inventoryBoxShortLabel,
  splitInventoryBox,
  type InventoryBoxEvent,
} from "@/lib/inventoryBoxApi";
import { inventoryBoxEventSummary } from "@/lib/inventoryBoxEventDisplay";
import {
  parseInboundSubcontainerUnitKind,
  type InboundArrivalUnitKind,
} from "@/pages/shipping/inboundArrivalUnitKind";

type SplitDraft = {
  quantity: string;
  unitKind: InboundArrivalUnitKind;
  picked: boolean;
};

function emptySplitDraft(unitKind: InboundArrivalUnitKind, picked = false): SplitDraft {
  return { quantity: "", unitKind, picked };
}

function formatEventWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function parseSplitQuantityDraft(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

function splitDraftAssignedTotal(drafts: SplitDraft[]): number {
  return drafts.reduce((sum, draft) => {
    const parsed = parseSplitQuantityDraft(draft.quantity);
    return sum + (parsed ?? 0);
  }, 0);
}

function splitDraftIsComplete(
  drafts: SplitDraft[],
  cartonQuantity: number,
  pickMode: boolean,
): boolean {
  if (drafts.length < 2) return false;
  if (pickMode) {
    if (drafts.some((draft) => (parseSplitQuantityDraft(draft.quantity) ?? 0) < 1)) return false;
    if (splitDraftAssignedTotal(drafts) !== cartonQuantity) return false;
    return drafts.some((draft) => draft.picked && (parseSplitQuantityDraft(draft.quantity) ?? 0) > 0);
  }
  if (!drafts.every((draft) => parseSplitQuantityDraft(draft.quantity) != null)) {
    return false;
  }
  if (splitDraftAssignedTotal(drafts) !== cartonQuantity) {
    return false;
  }
  return drafts.slice(1).some((draft) => (parseSplitQuantityDraft(draft.quantity) ?? 0) >= 1);
}

export function InventoryBoxTransferCards({
  boxHex,
  boxShortCode,
  unitKind,
  quantity,
  selectedPrinter,
  onChanged,
  onPendingChange,
  mode = "all",
  showHistory = true,
  pickActionLabel,
  onPickedAfterSplit,
  plain = false,
  onSubmitApi,
}: {
  boxHex: string;
  boxShortCode: string;
  unitKind: string;
  quantity: number;
  selectedPrinter: string;
  onChanged?: () => void;
  onPendingChange?: (pending: boolean) => void;
  mode?: "all" | "split" | "combine";
  showHistory?: boolean;
  /** Picking and packing add a checkbox so the operator can take only some of the new containers. */
  pickActionLabel?: "Pick" | "Pack";
  onPickedAfterSplit?: (
    pickedQuantity: number,
    selectedHexes: string[],
    unpickedHexes: string[],
  ) => void;
  /** Modal body: no extra card title, and the action button lives on the modal footer. */
  plain?: boolean;
  onSubmitApi?: (api: { submit: () => void; canSubmit: boolean; pending: boolean }) => void;
}): JSX.Element {
  const idPrefix = useId().replace(/:/g, "");
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const combineInputRef = useRef<HTMLInputElement | null>(null);
  const parsedUnitKind = parseInboundSubcontainerUnitKind(unitKind);
  const pickMode = pickActionLabel != null;
  const [splitDrafts, setSplitDrafts] = useState<SplitDraft[]>([
    emptySplitDraft(parsedUnitKind, pickMode),
    emptySplitDraft(parsedUnitKind, false),
  ]);
  const [combineOther, setCombineOther] = useState("");

  useEffect(() => {
    const nextUnitKind = parseInboundSubcontainerUnitKind(unitKind);
    setSplitDrafts([
      emptySplitDraft(nextUnitKind, pickActionLabel != null),
      emptySplitDraft(nextUnitKind, false),
    ]);
    setCombineOther("");
  }, [boxHex, pickActionLabel, quantity, unitKind]);

  useFloorPageScanCapture({
    inputRef: combineInputRef,
    autoFocus: false,
    onScan: (value) => {
      setCombineOther(value);
    },
  });

  const eventsQuery = useQuery({
    queryKey: ["inventory-box-events", boxHex],
    queryFn: () => fetchInventoryBoxEvents(boxHex),
    enabled: boxHex.trim() !== "",
  });
  const events = eventsQuery.data ?? [];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["inventory-box", boxHex] });
    void queryClient.invalidateQueries({ queryKey: ["inventory-box-events", boxHex] });
    onChanged?.();
  };

  const splitMutation = useMutation({
    mutationFn: () => {
      const printer = selectedPrinter.trim();
      if (printer === "") {
        throw new Error("Pick a label printer first.");
      }
      return splitInventoryBox(
        boxHex,
        splitDrafts.map((draft) => ({
          quantity: Number.parseInt(draft.quantity.trim(), 10),
          unit_kind: draft.unitKind,
        })),
        printer,
      );
    },
    onSuccess: (result) => {
      invalidate();
      if (pickActionLabel != null && onPickedAfterSplit != null) {
        const resultingBoxes = [result.box, ...result.created];
        const selectedHexes = resultingBoxes
          .filter((_, index) => splitDrafts[index]?.picked)
          .map((resultingBox) => resultingBox.hex);
        const unpickedHexes = resultingBoxes
          .filter((_, index) => splitDrafts[index]?.picked !== true)
          .map((resultingBox) => resultingBox.hex);
        const pickedQuantity = splitDrafts.reduce((sum, draft) => {
          if (!draft.picked) return sum;
          return sum + (parseSplitQuantityDraft(draft.quantity) ?? 0);
        }, 0);
        showToast({
          message: `Printed ${String(resultingBoxes.length)} container labels. ${pickActionLabel}ing ${String(pickedQuantity)}.`,
          tone: "success",
        });
        onPickedAfterSplit(pickedQuantity, selectedHexes, unpickedHexes);
        return;
      }
      const created = result.created.map((row) => row.short_code).join(", ");
      const emptiedThisContainer = result.box.quantity === 0;
      showToast({
        message:
          created === ""
            ? emptiedThisContainer
              ? "All quantity moved to new containers. This container is empty."
              : "Container split."
            : emptiedThisContainer
              ? `All quantity moved to new container labels: ${created}. This container is empty.`
              : `Split. New container labels: ${created}.`,
        tone: "success",
      });
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const combineMutation = useMutation({
    mutationFn: () => combineInventoryBox(boxHex, combineOther),
    onSuccess: (result) => {
      invalidate();
      setCombineOther("");
      showToast({
        message: `Combined ${result.emptied.short_code} into this container.`,
        tone: "success",
      });
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const busy = splitMutation.isPending || combineMutation.isPending;

  useEffect(() => {
    onPendingChange?.(busy);
  }, [busy, onPendingChange]);

  const showSplit = mode === "all" || mode === "split";
  const showCombine = mode === "all" || mode === "combine";
  const boxLabel = inventoryBoxShortLabel(boxShortCode);
  const combineButtonLabel = `Combine from other container into ${boxLabel}…`;

  const splitAssignedTotal = splitDraftAssignedTotal(splitDrafts);
  const splitRemainder = quantity - splitAssignedTotal;
  const splitComplete = splitDraftIsComplete(splitDrafts, quantity, pickMode);
  const pickedQuantity = pickMode
    ? splitDrafts.reduce((sum, draft) => {
      if (!draft.picked) return sum;
      return sum + (parseSplitQuantityDraft(draft.quantity) ?? 0);
    }, 0)
    : 0;
  const pickActionLower = pickActionLabel?.toLowerCase() ?? "";
  const splitCanSubmit = selectedPrinter.trim() !== "" && splitComplete;
  const combineCanSubmit = combineOther.trim() !== "";

  useEffect(() => {
    if (onSubmitApi == null) return;
    if (showCombine && !showSplit) {
      onSubmitApi({
        submit: () => combineMutation.mutate(),
        canSubmit: combineCanSubmit,
        pending: busy,
      });
      return;
    }
    if (showSplit && !showCombine) {
      onSubmitApi({
        submit: () => splitMutation.mutate(),
        canSubmit: splitCanSubmit,
        pending: busy,
      });
    }
  }, [
    busy,
    combineCanSubmit,
    combineMutation,
    onSubmitApi,
    showCombine,
    showSplit,
    splitCanSubmit,
    splitMutation,
  ]);

  useEffect(() => {
    if (!plain || !showCombine) return;
    combineInputRef.current?.focus();
  }, [boxHex, plain, showCombine]);

  return (
    <>
      {showSplit ? (
      <Card>
        {plain ? null : (
        <CardHeader>
          <CardTitleWithHelp hoverHint="Move quantity onto new unique containers. This container can stay at zero when everything moves (for example each labels). Pick a container type for each row. Quantities must add up to this container. New labels print automatically. Warehouse on-hand does not change.">
            Split into containers
          </CardTitleWithHelp>
        </CardHeader>
        )}
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-700">
            {pickActionLabel != null
              ? `Quantities must total ${quantity.toLocaleString()}. Check every resulting container that will be ${pickActionLower}ed. A new label prints for every container.`
              : `Quantities must total ${quantity.toLocaleString()}. The first row stays this container. A new label prints for every container.`}
          </p>
          <div className="space-y-3">
            {splitDrafts.map((draft, index) => (
              <div
                key={`split-${String(index)}`}
                className={cn(
                  "grid items-end gap-3 rounded-lg border border-slate-200 p-3",
                  pickMode
                    ? "sm:grid-cols-[auto_minmax(0,1fr)_12rem_auto]"
                    : "sm:grid-cols-[minmax(0,1fr)_12rem_auto]",
                )}
              >
                {pickActionLabel != null ? (
                  <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-slate-800">
                    <input
                      type="checkbox"
                      checked={draft.picked}
                      disabled={busy}
                      onChange={(event) => {
                        const next = [...splitDrafts];
                        next[index] = { ...draft, picked: event.target.checked };
                        setSplitDrafts(next);
                      }}
                    />
                    {pickActionLabel}
                  </label>
                ) : null}
                <div>
                  <Label htmlFor={`${idPrefix}-split-${String(index)}`}>
                    {index === 0
                      ? "This container quantity"
                      : `Container ${String(index + 1)} quantity`}
                  </Label>
                  <Input
                    id={`${idPrefix}-split-${String(index)}`}
                    className="text-base"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={draft.quantity}
                    disabled={busy}
                    onChange={(event) => {
                      const next = [...splitDrafts];
                      next[index] = { ...next[index], quantity: event.target.value };
                      setSplitDrafts(next);
                    }}
                  />
                </div>
                <InventoryBoxUnitKindSelect
                  id={`${idPrefix}-split-kind-${String(index)}`}
                  label="Container type"
                  value={draft.unitKind}
                  disabled={busy}
                  onChange={(nextUnitKind) => {
                    const next = [...splitDrafts];
                    next[index] = { ...next[index], unitKind: nextUnitKind };
                    setSplitDrafts(next);
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  aria-label={`Remove container ${String(index + 1)}`}
                  disabled={busy || splitDrafts.length <= 2}
                  onClick={() => setSplitDrafts(splitDrafts.filter((_, rowIndex) => rowIndex !== index))}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                const remainder = quantity - splitAssignedTotal;
                setSplitDrafts([
                  ...splitDrafts,
                  emptySplitDraft(parsedUnitKind, false),
                ].map((draft, index, rows) => (
                  index === rows.length - 1 && remainder > 0
                    ? { ...draft, quantity: String(remainder) }
                    : draft
                )));
              }}
            >
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              Add container
            </Button>
            <p className={splitRemainder === 0 ? "text-sm text-slate-600" : "text-sm text-rose-700"}>
              Total: {splitAssignedTotal.toLocaleString()} / {quantity.toLocaleString()}
              {pickActionLabel != null
                ? `. Selected to ${pickActionLower}: ${pickedQuantity.toLocaleString()}.`
                : "."}
            </p>
          </div>
          {selectedPrinter.trim() === "" ? (
            <p className="text-sm text-rose-700">Select a label printer first.</p>
          ) : null}
          {plain ? null : (
          <div className="flex flex-wrap justify-end">
            <Button
              type="button"
              disabled={busy || !splitCanSubmit}
              onClick={() => splitMutation.mutate()}
            >
              {splitMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              {pickActionLabel != null ? "Split, print, and continue" : "Split"}
            </Button>
          </div>
          )}
        </CardBody>
      </Card>
      ) : null}

      {showCombine ? (
      <Card>
        {plain ? null : (
        <CardHeader>
          <CardTitleWithHelp hoverHint="Pour another unique container of the same SKU at this location into this one. The other container is emptied. Warehouse on-hand does not change.">
            Combine with container
          </CardTitleWithHelp>
        </CardHeader>
        )}
        <CardBody className="space-y-3">
          <div>
            <Label htmlFor={`${idPrefix}-combine-other`}>
              {plain ? "Container to be combined with" : "Other container"}
            </Label>
              <Input
                ref={combineInputRef}
                id={`${idPrefix}-combine-other`}
                className="text-base"
                type="text"
                autoComplete="off"
                placeholder="Scan Data Matrix or type container code"
                value={combineOther}
                disabled={busy}
                onChange={(event) => setCombineOther(event.target.value)}
              />
          </div>
          {plain ? null : (
          <Button
            type="button"
            disabled={busy || !combineCanSubmit}
            onClick={() => combineMutation.mutate()}
          >
            {combineMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {combineButtonLabel}
          </Button>
          )}
        </CardBody>
      </Card>
      ) : null}

      {showHistory ? (
      <Card className="flex min-h-0 flex-col">
        <CardHeader>
          <CardTitleWithHelp hoverHint="Created, approved quantity saves, reprints, splits, and combines for this container. Approved quantity saves post a Megaventory inventory adjustment for the difference.">
            History
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody className="min-h-0">
          {eventsQuery.isLoading ? (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading history.
            </p>
          ) : events.length === 0 ? (
            <p className="text-sm text-slate-500">No history recorded for this container yet.</p>
          ) : (
            <div className="max-h-[min(24rem,50vh)] overflow-auto">
              <table className="w-full border-collapse text-left text-sm">
                <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white">
                  <tr>
                    <th className="px-3 py-2 font-medium text-slate-600">When</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Who</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Change</th>
                    <th className="px-3 py-2 text-right font-medium tabular-nums text-slate-600">
                      Megaventory
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event: InventoryBoxEvent) => (
                    <tr key={event.id} className="border-b border-slate-100 hover:bg-slate-50/80 hover:[&>td]:bg-slate-50/80">
                      <td className="px-3 py-2 text-slate-700">{formatEventWhen(event.created_at)}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {event.actor_display_name?.trim() || "operator"}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{inventoryBoxEventSummary(event)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {event.mv_document_id != null ? String(event.mv_document_id) : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      ) : null}
    </>
  );
}

export function InventoryBoxSplitCombineModal({
  open,
  mode,
  boxHex,
  boxShortCode,
  unitKind,
  quantity,
  selectedPrinter,
  station = "",
  onStationChange,
  unavailableMessage,
  onClose,
  onChanged,
  onPendingChange,
  pickActionLabel,
  onPickedAfterSplit,
}: {
  open: boolean;
  mode: "split" | "combine";
  boxHex: string;
  boxShortCode: string;
  unitKind: string;
  quantity: number;
  selectedPrinter: string;
  station?: string;
  onStationChange?: (station: string) => void;
  unavailableMessage?: string | null;
  onClose: () => void;
  onChanged?: () => void;
  onPendingChange?: (pending: boolean) => void;
  pickActionLabel?: "Pick" | "Pack";
  onPickedAfterSplit?: (
    pickedQuantity: number,
    selectedHexes: string[],
    unpickedHexes: string[],
  ) => void;
}): JSX.Element | null {
  const [pending, setPending] = useState(false);
  const submitRef = useRef({ submit: () => {}, canSubmit: false, pending: false });
  const [submitReady, setSubmitReady] = useState({ canSubmit: false, pending: false });
  const onSubmitApi = useCallback((api: { submit: () => void; canSubmit: boolean; pending: boolean }) => {
    submitRef.current = api;
    setSubmitReady((previous) => (
      previous.canSubmit === api.canSubmit && previous.pending === api.pending
        ? previous
        : { canSubmit: api.canSubmit, pending: api.pending }
    ));
  }, []);
  const titleAction = mode === "split" ? "Split" : "Combine";
  const footerAction = mode === "combine"
    ? "Combine"
    : pickActionLabel != null
      ? "Split, print, and continue"
      : "Split";

  useEffect(() => {
    onPendingChange?.(pending);
  }, [onPendingChange, pending]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || pending) return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, open, pending]);

  if (!open) return null;

  return createPortal(
    <div {...hubNestedModalOverlayProps(true)}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="inventory-box-split-combine-title"
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-slate-50 shadow-xl"
      >
        <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
          <h2 id="inventory-box-split-combine-title" className="text-lg font-semibold text-slate-900">
            {titleAction} {inventoryBoxShortLabel(boxShortCode)}
          </h2>
          {onStationChange != null ? (
            <InboundZplPrinterHeaderButton
              station={station}
              onStationChange={onStationChange}
              unavailableMessage={unavailableMessage}
            />
          ) : null}
        </header>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <InventoryBoxTransferCards
            boxHex={boxHex}
            boxShortCode={boxShortCode}
            unitKind={unitKind}
            quantity={quantity}
            selectedPrinter={selectedPrinter}
            mode={mode}
            showHistory={false}
            plain
            pickActionLabel={pickActionLabel}
            onPickedAfterSplit={onPickedAfterSplit}
            onChanged={onChanged}
            onPendingChange={setPending}
            onSubmitApi={onSubmitApi}
          />
        </div>
        <footer className="flex shrink-0 justify-end gap-2 border-t border-slate-200 bg-white px-4 py-3">
          <Button
            type="button"
            disabled={pending || submitReady.pending || !submitReady.canSubmit}
            onClick={() => submitRef.current.submit()}
          >
            {submitReady.pending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            {footerAction}
          </Button>
          <Button type="button" variant="secondary" disabled={pending || submitReady.pending} onClick={onClose}>
            Cancel
          </Button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
